import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiUsers, 
  FiFileText, 
  FiMapPin, 
  FiSettings,
  FiDatabase,
  FiFolder,
  FiAlertCircle,
  FiHome,
  FiPlus,
  FiCalendar,
  FiChevronDown,
  FiVideo
} from 'react-icons/fi';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHomePageDropdown, setShowHomePageDropdown] = useState(false);

  useEffect(() => {
    // No data fetching needed, just set loading to false
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingSpinner>Loading dashboard...</LoadingSpinner>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <Title>Dashboard</Title>
      </DashboardHeader>

        <ManagementSection>
        <SectionTitle>Management</SectionTitle>
        <ManagementGrid>
          <ManagementCard onClick={() => navigate('/event-requests')}>
            <ManagementIcon>
              <FiAlertCircle />
            </ManagementIcon>
            <ManagementContent>
              <ManagementTitle>Event Requests</ManagementTitle>
              <ManagementDescription>View and manage event planning requests</ManagementDescription>
            </ManagementContent>
          </ManagementCard>

          <HomePageManagementWrapper>
            <ManagementCard onClick={() => setShowHomePageDropdown(!showHomePageDropdown)}>
              <ManagementIcon>
                <FiHome />
              </ManagementIcon>
              <ManagementContent>
                <ManagementTitle>
                  Home Page Management
                  <FiChevronDown style={{ float: 'right', transform: showHomePageDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                </ManagementTitle>
                <ManagementDescription>Manage home page content and layout</ManagementDescription>
              </ManagementContent>
            </ManagementCard>
            
            {showHomePageDropdown && (
              <DropdownMenu>
                <DropdownItem onClick={(e) => { e.stopPropagation(); navigate('/homepage-management'); }}>
                  <FiHome /> Main Page Management
                </DropdownItem>
                <DropdownItem onClick={(e) => { e.stopPropagation(); navigate('/services-hero'); }}>
                  <FiVideo /> Services Hero Section
                </DropdownItem>
                <DropdownItem onClick={(e) => { e.stopPropagation(); navigate('/calendar-management'); }}>
                  <FiCalendar /> 31-Day Calendar
                </DropdownItem>
              </DropdownMenu>
            )}
          </HomePageManagementWrapper>

          <ManagementCard onClick={() => navigate('/manage-upcoming-events')}>
            <ManagementIcon>
              <FiPlus />
            </ManagementIcon>
            <ManagementContent>
              <ManagementTitle>Event Posting</ManagementTitle>
              <ManagementDescription>Create and manage event postings</ManagementDescription>
            </ManagementContent>
          </ManagementCard>

          <ManagementCard onClick={() => console.log('Events functionality coming soon')}>
            <ManagementIcon>
              <FiCalendar />
            </ManagementIcon>
            <ManagementContent>
              <ManagementTitle>Events</ManagementTitle>
              <ManagementDescription>View and manage all events</ManagementDescription>
            </ManagementContent>
          </ManagementCard>

          <ManagementCard onClick={() => navigate('/tickets')}>
            <ManagementIcon>
              <FiFileText />
            </ManagementIcon>
            <ManagementContent>
              <ManagementTitle>Tickets Management</ManagementTitle>
              <ManagementDescription>Manage tickets, attendees and sales</ManagementDescription>
            </ManagementContent>
          </ManagementCard>
        </ManagementGrid>
      </ManagementSection>

      <QuickActionsSection>
        <SectionTitle>Quick Actions</SectionTitle>
        <QuickActionsGrid>
          <QuickActionCard onClick={() => navigate('/users')}>
            <QuickActionIcon>
              <FiUsers />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>Manage Users</QuickActionTitle>
              <QuickActionDescription>View and manage user accounts</QuickActionDescription>
            </QuickActionContent>
          </QuickActionCard>

          <QuickActionCard onClick={() => navigate('/supported-regions')}>
            <QuickActionIcon>
              <FiMapPin />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>Supported Regions</QuickActionTitle>
              <QuickActionDescription>Manage supported regions and countries</QuickActionDescription>
            </QuickActionContent>
          </QuickActionCard>

          <QuickActionCard onClick={() => navigate('/categories')}>
            <QuickActionIcon>
              <FiFolder />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>Categories</QuickActionTitle>
              <QuickActionDescription>Manage categories and subcategories</QuickActionDescription>
            </QuickActionContent>
          </QuickActionCard>

          <QuickActionCard onClick={() => navigate('/raw-data')}>
            <QuickActionIcon>
              <FiDatabase />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>Raw Data</QuickActionTitle>
              <QuickActionDescription>Access and manage database collections</QuickActionDescription>
            </QuickActionContent>
          </QuickActionCard>
        </QuickActionsGrid>
      </QuickActionsSection>
    </DashboardContainer>
  );
};

// Styled Components
const DashboardContainer = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.2rem;
  color: #666;
`;

const DashboardHeader = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
    z-index: -1;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const StatIcon = styled.div`
  background: linear-gradient(135deg, #000 0%, #333 100%);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #000;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.25rem;
`;

const StatTrend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.positive ? '#22c55e' : '#ef4444'};
`;

const ManagementSection = styled.div`
  margin-bottom: 3rem;
`;

const ManagementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ManagementCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ManagementIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.theme.colors.dark};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const ManagementContent = styled.div`
  flex: 1;
`;

const ManagementTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 0.25rem;
`;

const ManagementDescription = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.4;
`;

const QuickActionsSection = styled.div`
  margin-top: auto;
  padding-top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 1rem;
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const QuickActionCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const QuickActionIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.theme.colors.primary};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const QuickActionContent = styled.div`
  flex: 1;
`;

const QuickActionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 0.25rem;
`;

const QuickActionDescription = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.4;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }
`;

const ChartHeader = styled.div`
  margin-bottom: 1rem;
`;

const ChartTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #000;
  margin: 0;
`;

const ChartSubtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0.25rem 0 0 0;
`;

const RecentUsersList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #000;
`;

const UserEmail = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-top: 2px;
`;

const UserTime = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #666;
`;

const CountryList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const CountryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const CountryName = styled.div`
  font-weight: 600;
  color: #000;
`;

const CountryCount = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  margin: 20px;
`;

const ChartContainer = styled.div`
  margin-top: 1rem;
`;

const PieLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const LegendText = styled.span`
  font-size: 0.85rem;
  color: #666;
`;

const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ActivityCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
`;

const QuickActionsCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
`;

const CardHeader = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #000;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
  background: ${props => {
    switch(props.type) {
      case 'user': return 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
      case 'post': return 'linear-gradient(135deg, #10b981 0%, #047857 100%)';
      case 'region': return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      default: return 'linear-gradient(135deg, #6b7280 0%, #374151 100%)';
    }
  }};
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityAction = styled.div`
  font-weight: 500;
  color: #000;
  font-size: 0.9rem;
`;

const ActivityUser = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
`;

const ActivityTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #666;
  flex-shrink: 0;
`;

const QuickActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #000;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateX(4px);
  }
`;

const RawDataButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(0, 123, 255, 0.8);
  border: 1px solid rgba(0, 123, 255, 1);
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 123, 255, 1);
    border-color: rgba(0, 123, 255, 1);
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
  }
  
  svg {
    font-size: 14px;
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    color: #333;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }
  
  input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #007bff;
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: #0056b3;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

// Add User Modal Component
const AddUserModal = ({ onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onUserAdded();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to add user');
      }
    } catch (err) {
      setError('Failed to add user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>Add New User</h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          {error && <ModalErrorMessage>{error}</ModalErrorMessage>}
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
              />
            </FormGroup>
            <ModalActions>
              <CancelButton type="button" onClick={onClose}>Cancel</CancelButton>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add User'}
              </SubmitButton>
            </ModalActions>
          </form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const HomePageManagementWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  margin-top: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 10;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownItem = styled.div`
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${props => props.theme.colors.dark};
  font-size: 0.95rem;

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.1rem;
  }

  &:hover {
    background: #f5f5f5;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

export default Dashboard;
